import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, finalize, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  GapCalculator,
  GapCalculatorInterface,
} from '../../../interfaces/gap-calculator.interface';
import { DepreciationChartService } from '../../modules/depreciation-chart/depreciation-chart.service';
import { ApiService } from '../../services/api.service';
import { isClassicGapHost } from '../../shared/brand';
import { gapFormUrl } from '../../shared/form-url';
import { generateYearsSelectOptions } from '../../shared/year-options.util';
import { finiteAmount } from '../../shared/vehicle-depreciation.util';
import { positiveIntOnly } from '../apk/apk/validators';
import { PolicyCreatorInitDataService } from '../policy-creator/policy-creator-init-data.service';
import { OverlayPanel } from 'primeng/overlaypanel';

interface CalculateOfferHomePayload {
  sellerNodeCode: string;
  productCode: string;
  saleInitiatedOn: string;
  endorsementTypeCode: string;
  vehicleSnapshot: VehicleSnapshot;
  options: Options;
}

interface VehicleSnapshot {
  categoryCode: string;
  makeCode: string;
  modelCode: string;
  usageTypeCode: string;
  purchasePrice: number;
  purchasePriceNet: number;
  purchasePriceInputType: string;
  purchasePriceVatReclaimableCode: string;
  firstRegisteredOn: string;
  purchasedOn: string;
  mileage: number | null;
  usageCode: string;
  evaluationDate?: string;
}

interface Options {
  CLAIM_LIMIT: string;
  TERM: string;
  PAYMENT_TERM: string;
  PAYMENT_METHOD: string;
}

interface YearOption {
  year: string;
}

interface ClaimLimitOption {
  value: number;
  name: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('calculatorRef') calculatorRef: ElementRef<HTMLElement>;

  gapCalculatorDto: GapCalculatorInterface;
  gapCalculatorFormGroup: UntypedFormGroup;

  gapSliderFormGroup: UntypedFormGroup;

  years: YearOption[];
  yearsDirect: YearOption[];
  claimLimits: ClaimLimitOption[];
  selectedRegDate: string;
  vehiclePurchasePrice: number;
  selectedClaimLimit: number;
  calculatedPremium: number;
  calculationError = false;
  insTermOptions: number[];
  insTermOptionsDirect: number[];
  typeOfPolicy = 'invoice';
  loadingCalculation = false;
  isBrowser: boolean;
  isMobile = false;
  readonly formUrl = gapFormUrl;
  /** Wait after the last price keystroke before recalculating the payout. */
  readonly simulationDebounceMs = 300;
  private premiumWasCalculated = false;
  private lastPremiumPrice: number | null = null;
  private premiumRequestSeq = 0;
  private readonly destroy$ = new Subject<void>();

  get calculateInactive(): boolean {
    if (this.loadingCalculation) {
      return true;
    }

    if (!this.gapCalculatorFormGroup?.valid) {
      return true;
    }

    if (
      this.typeOfPolicy === 'direct' &&
      !this.gapCalculatorFormGroup.controls.claimLimit.value
    ) {
      return true;
    }

    return (
      this.typeOfPolicy !== 'direct' &&
      !this.gapCalculatorFormGroup.controls.vehiclePurchasePrice.value
    );
  }

  constructor(
    private fb: UntypedFormBuilder,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private depreciationChartService: DepreciationChartService,
    private breakpointObserver: BreakpointObserver,
    private policyCreatorInitDataService: PolicyCreatorInitDataService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.claimLimits = [{ value: 100000, name: '100 000 zł' }];
    this.insTermOptions = [12, 24, 36, 48, 60];
    this.insTermOptionsDirect = [12, 24, 36];
  }

  ngAfterViewInit(): void {
    this.route.fragment.subscribe((fragment) => {
      /*
        hack: for some reason without setTimout method the document cannot find element of id 'fragment'
        even if event listener 'DOMContentLoaded' is here, so the best solution is to add 'setTimout' and wait until
        element can be found
      */
      if (this.isBrowser) {
        if (!this.document.getElementById(fragment)) {
          setTimeout(() => {
            const element = this.document.getElementById(fragment);
            if (element) {
              element.scrollIntoView({ behavior: 'auto' });
            }
          }, 200);
        } else {
          const element = this.document.getElementById(fragment);
          if (element) {
            element.scrollIntoView({ behavior: 'auto' });
          }
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const affiliationLink = params.get('partner');
      if (affiliationLink && this.isBrowser) {
        window.location.assign(
          gapFormUrl(`/new-policy/1?partner=${encodeURIComponent(affiliationLink)}`),
        );
      }
    });

    this.years = generateYearsSelectOptions(new Date(), 11);
    this.yearsDirect = generateYearsSelectOptions(new Date(), 11);
    this.initializeForm();
    this.depreciationChartService.initializeSliderForm();

    this.gapSliderFormGroup = this.depreciationChartService.gapSliderFormGroup;
    this.syncVehiclePriceFields();
    this.watchSimulationPrice();
    this.watchPremiumPrice();

    this.breakpointObserver
      .observe('(max-width: 767px)')
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => (this.isMobile = state.matches));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onVehiclePriceBlur(): void {
    this.handleSliding();
    this.recalculatePremiumIfNeeded();
  }

  /**
   * Amount safe to pass into `number` pipe. Hides the payout when the
   * simulation has not been recalculated or the value is not numeric.
   */
  simulationAmount(value: unknown): number | null {
    return finiteAmount(value);
  }

  setActualYearPlaceholder(): string {
    return moment().format('YYYY');
  }

  handleYearSelection(event: { value: YearOption }): void {
    this.selectedRegDate = `${event.value.year}-01-01`;
  }

  handleLimitSelection(event: {
    originalEvent: Event;
    value: ClaimLimitOption;
  }): void {
    this.selectedClaimLimit = event.value.value;
    this.gapCalculatorFormGroup.get('claimLimit').setValue(event.value);
    this.gapCalculatorFormGroup.controls.vehiclePurchasePrice.setValue(1);
  }

  ngAfterViewChecked(): void {
    // Range slider keeps the numbers on `values`, while the template binds
    // aria-valuenow to `value` and clears it on every check.
    if (!this.isBrowser || !this.gapSliderFormGroup) {
      return;
    }

    const raw = this.gapSliderFormGroup.controls.monthsArray.value;
    const values = Array.isArray(raw) ? raw : [];
    const handles = this.document.querySelectorAll('p-slider .p-slider-handle');

    handles.forEach((handle, index) => {
      handle.setAttribute('role', 'slider');
      const current = Number(values[index]);
      handle.setAttribute(
        'aria-valuenow',
        Number.isFinite(current) ? String(current) : '0',
      );
      if (
        !handle.getAttribute('aria-label') &&
        !handle.getAttribute('aria-labelledby')
      ) {
        handle.setAttribute(
          'aria-label',
          index === 0
            ? 'Początek okresu użytkowania'
            : 'Koniec okresu użytkowania',
        );
      }
    });
  }

  handleSliding() {
    this.depreciationChartService.handleSliding();
  }

  onSubmit(): void {
    if (this.calculateInactive) {
      return;
    }
    this.runPremiumCalculation();
  }

  private runPremiumCalculation(): void {
    this.loadingCalculation = true;
    this.calculationError = false;
    this.calculatedPremium = null;
    this.gapCalculatorDto = { ...this.gapCalculatorFormGroup.value };

    if (this.typeOfPolicy === 'direct') {
      this.gapCalculatorDto.claimLimit = this.selectedClaimLimit;
    } else {
      this.gapCalculatorDto.claimLimit = null;
    }

    this.gapCalculatorDto.vehicleRegDate = this.resolveVehicleRegDate();
    const requestSeq = ++this.premiumRequestSeq;
    this.lastPremiumPrice =
      this.gapCalculatorFormGroup.controls.vehiclePurchasePrice.value;

    const calculationPayload: CalculateOfferHomePayload = {
      sellerNodeCode: environment.SELLER_NODE_CODE,
      productCode: null,
      saleInitiatedOn: moment().format('YYYY-MM-DD'), // '2024-07-16T07:26:57Z',
      endorsementTypeCode: 'NEW_BUSINESS',
      vehicleSnapshot: {
        categoryCode: 'PC',
        makeCode: '209',
        modelCode: '29',
        usageTypeCode: 'INDIVIDUAL',
        purchasePrice: this.gapCalculatorDto.vehiclePurchasePrice * 100,
        purchasePriceNet:
          Math.round(this.gapCalculatorDto.vehiclePurchasePrice / 1.23) * 100,
        purchasePriceInputType: 'WITH_VAT',
        purchasePriceVatReclaimableCode: 'NO',
        firstRegisteredOn: moment(this.gapCalculatorDto.vehicleRegDate).format(
          'YYYY-MM-DD',
        ),
        purchasedOn: moment().format('YYYY-MM-DD'),
        mileage: null,
        usageCode: 'STANDARD',
      },
      options: {
        CLAIM_LIMIT: null,
        TERM: `T_${this.gapCalculatorDto.insTerm}`,
        PAYMENT_TERM: 'PT_LS',
        PAYMENT_METHOD: 'PM_BT',
      },
    };

    if (this.typeOfPolicy === 'invoice') {
      calculationPayload.productCode = '5_DCGAP_M25_GEN';
      calculationPayload.options.CLAIM_LIMIT = 'CL_50000';
    }

    if (this.typeOfPolicy === 'casco') {
      calculationPayload.productCode = '5_DCGAP_MG25_GEN';
      calculationPayload.vehicleSnapshot.purchasedOn = moment()
        .subtract(6, 'months')
        .format('YYYY-MM-DD');
      calculationPayload.vehicleSnapshot.evaluationDate =
        moment().format('YYYY-MM-DD');
      calculationPayload.options.CLAIM_LIMIT = 'CL_50000';
    }

    if (this.typeOfPolicy === 'direct') {
      calculationPayload.productCode = '5_DCGAP_F25_GEN';
      calculationPayload.options.CLAIM_LIMIT = `CL_${this.gapCalculatorDto.claimLimit}`;
      calculationPayload.vehicleSnapshot.purchasePrice = 1;
    }

    this.apiService
      .getCalculation(calculationPayload)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          // GAP-487: never leave the mobile calculator stuck on the spinner
          // even if the response body is unexpected or CD skips an update.
          // Ignore a response that a newer price change already replaced.
          if (requestSeq !== this.premiumRequestSeq) {
            return;
          }
          this.loadingCalculation = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (requestSeq !== this.premiumRequestSeq) {
            return;
          }
          const premium = response?.premiumSuggested;
          if (typeof premium === 'number' && Number.isFinite(premium)) {
            this.calculatedPremium = Math.round(premium / 100);
            this.premiumWasCalculated = true;
            this.calculationError = false;
            // Warm sodan + portfolios before the user taps "Wybierz".
            void this.policyCreatorInitDataService.ensureCalculationDataLoaded();
          } else {
            this.calculatedPremium = null;
            this.calculationError = true;
          }
          this.cdr.detectChanges();
        },
        error: () => {
          if (requestSeq !== this.premiumRequestSeq) {
            return;
          }
          this.calculatedPremium = null;
          this.calculationError = true;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * GAP-487: skip /cta/buy-gap — go straight to the policy calculator while
   * sodan-reply-heads + question replies (+ portfolios) load in the background.
   */
  async startPolicyFlow(event?: Event): Promise<void> {
    event?.preventDefault();
    if (!this.isBrowser) {
      return;
    }

    void this.policyCreatorInitDataService.ensureCalculationDataLoaded();

    if (isClassicGapHost()) {
      await this.router.navigateByUrl('/new-policy/1');
      return;
    }

    window.location.assign(gapFormUrl('/new-policy/1'));
  }

  selectTypeOfPolicy(type): void {
    this.typeOfPolicy = type;
    this.calculatedPremium = null;
    this.calculationError = false;
    this.premiumWasCalculated = false;
    this.lastPremiumPrice = null;
  }

  initializeForm(): void {
    this.gapCalculatorDto = new GapCalculator();
    this.gapCalculatorFormGroup = this.fb.group({
      vehicleRegDate: [this.gapCalculatorDto.insTerm, [Validators.required]],
      insTerm: [this.gapCalculatorDto.insTerm, [Validators.required]],
      claimLimit: [this.gapCalculatorDto.claimLimit],
      vehiclePurchasePrice: [
        this.gapCalculatorDto.vehiclePurchasePrice,
        [positiveIntOnly(), RxwebValidators.maxNumber({ value: 600000 })],
      ],
    });
  }

  hideOverlayPanel(overlayPanel: OverlayPanel): void {
    if (overlayPanel) {
      overlayPanel.hide();
    }
  }

  /**
   * Keeps both vehicle-price fields on the same value.
   * Left → right still emits, so the debounced payout runs.
   * Right → left also emits, so a price typed on the right refreshes the premium.
   * The direct-policy sentinel (1) stays out of the simulation field.
   */
  private syncVehiclePriceFields(): void {
    const calculatorPrice =
      this.gapCalculatorFormGroup.controls.vehiclePurchasePrice;
    const simulationPrice =
      this.gapSliderFormGroup.controls.vehiclePurchasePrice;

    calculatorPrice.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((price) => {
        if (this.typeOfPolicy === 'direct' || simulationPrice.value === price) {
          return;
        }

        simulationPrice.setValue(price);
      });

    simulationPrice.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((price) => {
        if (calculatorPrice.value === price) {
          return;
        }

        calculatorPrice.setValue(price);
      });
  }

  /** Recalculate the payout after the price stops changing. Blur calls handleSliding immediately. */
  private watchSimulationPrice(): void {
    this.gapSliderFormGroup.controls.vehiclePurchasePrice.valueChanges
      .pipe(
        debounceTime(this.simulationDebounceMs),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.handleSliding());
  }

  /**
   * After a premium is already on screen, a later price edit runs the same
   * quote as "Oblicz składkę", once the user pauses.
   */
  private watchPremiumPrice(): void {
    this.gapCalculatorFormGroup.controls.vehiclePurchasePrice.valueChanges
      .pipe(
        debounceTime(this.simulationDebounceMs),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.recalculatePremiumIfNeeded());
  }

  private recalculatePremiumIfNeeded(): void {
    if (!this.premiumWasCalculated || !this.gapCalculatorFormGroup?.valid) {
      return;
    }

    const price =
      this.gapCalculatorFormGroup.controls.vehiclePurchasePrice.value;
    if (
      price === this.lastPremiumPrice ||
      (this.typeOfPolicy !== 'direct' && !price) ||
      (this.typeOfPolicy === 'direct' &&
        !this.gapCalculatorFormGroup.controls.claimLimit.value)
    ) {
      return;
    }

    this.runPremiumCalculation();
  }

  private resolveVehicleRegDate(): string {
    if (this.selectedRegDate) {
      return this.selectedRegDate;
    }

    const yearControl = this.gapCalculatorFormGroup?.controls?.vehicleRegDate
      ?.value;
    const year =
      typeof yearControl === 'object' && yearControl?.year
        ? yearControl.year
        : yearControl;

    if (year) {
      return `${year}-01-01`;
    }

    return moment().format('YYYY-01-01');
  }
}
